import type Phaser from "phaser";
import { Capacitor } from "@capacitor/core";
import {
  capBottomInset,
  deriveShellLayout,
  type ShellLayout,
} from "../data/balance.js";
import { ZERO_INSETS, type SafeAreaSource } from "@yjsgames/safe-area";
import { shouldReserveBanner } from "@yjsgames/ad-ports";
import { setActiveLayout } from "../ui/sceneLayout.js";
import { FONT_FAMILY, TEXT_RESOLUTION, TYPO } from "../ui/uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import { resolveBannerReserveWorldPx } from "./gameSceneBannerReserve.js";

export interface GameSceneLayoutResult {
  readonly layout: ShellLayout;
  readonly bannerPreviewBox?: Phaser.GameObjects.Container;
}

export interface ApplyGameSceneLayoutInput {
  readonly scene: Phaser.Scene;
  readonly registry: Phaser.Data.DataManager;
  readonly featureBannerEnabled: boolean;
  readonly adFree: boolean;
  readonly bannerPreviewEnabled: boolean;
  readonly previousBannerPreviewBox?: Phaser.GameObjects.Container;
}

export function applyGameSceneLayout(input: ApplyGameSceneLayoutInput): GameSceneLayoutResult {
  const safeAreaSource = input.registry.get("safeAreaSource") as SafeAreaSource | undefined;
  const rawInsets = safeAreaSource?.read() ?? ZERO_INSETS;
  const insets = capBottomInset(rawInsets);
  const isNative = Capacitor.isNativePlatform();
  const reserve = shouldReserveBanner({
    isNative,
    featureEnabled: input.featureBannerEnabled,
    adFree: input.adFree,
    previewOverride: input.bannerPreviewEnabled,
  })
    ? resolveBannerReserveWorldPx({
        isNative,
        screenCssWidth:
          input.scene.game.canvas?.clientWidth ||
          (typeof window === "undefined" ? 0 : window.innerWidth) ||
          input.scene.scale.width,
        designWidth: input.scene.scale.width,
        safeTopWorld: insets.top,
      })
    : 0;
  const layout = deriveShellLayout(insets, input.scene.scale.height, reserve);
  setActiveLayout(input.scene, layout, { ...insets, top: insets.top + reserve });
  return {
    layout,
    bannerPreviewBox: renderBannerPreviewPlaceholder(
      input.scene,
      input.previousBannerPreviewBox,
      reserve,
      insets.top,
      input.bannerPreviewEnabled,
    ),
  };
}

function renderBannerPreviewPlaceholder(
  scene: Phaser.Scene,
  previous: Phaser.GameObjects.Container | undefined,
  reserveWorldPx: number,
  insetTop: number,
  enabled: boolean,
): Phaser.GameObjects.Container | undefined {
  previous?.destroy();
  if (reserveWorldPx <= 0 || !enabled) return undefined;
  const w = scene.scale.width;
  const rect = scene.add
    .rectangle(0, insetTop, w, reserveWorldPx, PALETTE_NUM.overlayDim, 0.66)
    .setOrigin(0, 0)
    .setStrokeStyle(1, PALETTE_NUM.accentGold, 0.8);
  const label = scene.add
    .text(w / 2, insetTop + reserveWorldPx / 2, "배너광고 영역 (DEV 미리보기)", {
      fontFamily: FONT_FAMILY,
      fontSize: TYPO.caption,
      color: PALETTE.accentGold,
    })
    .setOrigin(0.5);
  label.setResolution(TEXT_RESOLUTION);
  return scene.add.container(0, 0, [rect, label]).setDepth(DEPTH.bannerPreview);
}
